import React from 'react';
import { C } from '../constants';
import { currentClaims, audience, profileLabel, isTecnico } from '../services/auth';
import { SERVICE_CATALOG } from '../services/catalog';
import { veMonitoreo, serviciosAccesibles } from '../services/acceso';
import { CATEGORIAS } from '../services/productos';
import DashboardTecnico from './DashboardTecnico';
import ServiceIcon from './ServiceIcon';

// ─────────────────────────────────────────────────────────────────────────────
// HOME — DASHBOARD role-aware de la suite Solar Hub (Alebas). Al iniciar sesión
// presenta KPIs + accesos según el rol de la identidad compartida con FluxAI:
//   - Cliente (user)  → SUS cotizaciones (total/en gestión/ganadas/inversión) + cotizar.
//   - Admin/operador  → KPIs de plataforma + MONITOR DE TODOS LOS SERVICIOS.
//   - Técnico         → registro RETIE / sus proyectos.   - Proveedor → su portal.
// El gateo es por audiencia (× entitlement con el login real de api/).
// ─────────────────────────────────────────────────────────────────────────────

const fmtCOP = n => n != null ? '$' + Math.round(n).toLocaleString('es-CO') : '$0';

function Card({ children, style, onClick, hover }) {
  const [h, setH] = React.useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        background: C.card, border: `1px solid ${h && hover ? C.teal : C.border || '#1b2c3a'}`,
        borderRadius: 8, padding: 14, transition: 'all .15s', cursor: onClick ? 'pointer' : 'default',
        transform: h && hover ? 'translateY(-2px)' : 'none',
        boxShadow: h && hover ? `0 8px 28px rgba(1,112,139,0.18)` : 'none', ...style,
      }}>
      {children}
    </div>
  );
}

// Paleta SOBRIA (software, no web): un solo acento (teal) + estado (verde OK / rojo negativo) +
// neutros. Los KPI van todos en color de texto neutro — sin "arcoíris" de números.
function Kpi({ label, value }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 12px', borderLeft: `2px solid ${C.teal}66` }}>
      <div style={{ fontSize: 9.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 800, color: C.heading, marginTop: 2, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

// Encabezado de sección uniforme (estilo consola: minúsculo, mayúsculas espaciadas) con
// acción opcional a la derecha. Una sola "imagen de software" en toda la app.
function SectionTitle({ children, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, margin: '8px 0 8px' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: 0.8 }}>{children}</div>
      {right}
    </div>
  );
}

// Botón de acción sobrio (link de consola), NO botón web grande. Reutilizable.
function LinkBtn({ onClick, children }) {
  return (
    <button type="button" className="sh-btn sh-btn-outline sh-btn-sm" onClick={onClick} style={{ color: C.teal, borderColor: `${C.teal}44` }}>{children}</button>
  );
}

const FLOW = [
  ['Cotización', 'Dimensiona y cotiza con motor de simulación horaria (TMY, sombras, financiero 25 años).'],
  ['Venta', 'Estados nuevo → propuesta → negociación → ganada; seguimiento público.'],
  ['Técnico', 'Matching RETIE por score (rating·carga·experiencia) y asignación.'],
  ['Proveedor', 'Orden de compra al proveedor, comisiones y entrega.'],
  ['Monitoreo', 'Telemetría en tiempo real, potenciada por FluxAI (medido vs simulado).'],
];

function Tile({ title, desc, badge, onClick }) {
  return (
    <Card hover onClick={onClick} style={{ display: 'flex', flexDirection: 'column', gap: 5, minHeight: 92, borderLeft: `3px solid ${C.teal}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.heading }}>{title}</div>
        {badge && (
          <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6,
            color: C.muted, background: `${C.muted}14`, border: `1px solid ${C.border}`, borderRadius: 5, padding: '3px 7px', whiteSpace: 'nowrap' }}>{badge}</span>
        )}
      </div>
      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.45 }}>{desc}</div>
    </Card>
  );
}

export default function Home({ go, openFlux, quotes = [], ordenes = [], sincro = {} }) {
  const claims = currentClaims();
  const aud = audience(claims);
  const esAdmin = aud === 'admin';
  const esOper = aud === 'operator';
  const esCliente = aud === 'user';
  const email = (claims?.email || '').toLowerCase().trim();
  const nombre = (claims?.name || '').split(' ')[0] || '';
  if (isTecnico(claims)) {
    return <DashboardTecnico go={go} quotes={quotes} ordenes={ordenes} sincro={sincro} />;
  }

  // Alcance de cotizaciones: el cliente ve LAS SUYAS; admin/operador ven TODAS.
  const scope = esCliente ? quotes.filter(q => (q.email || '').toLowerCase().trim() === email) : quotes;
  const total = scope.length;
  const ganadas = scope.filter(q => q.status === 'ganada' || q.status === 'cerrada').length;
  const enGestion = scope.filter(q => ['nuevo', 'enviada', 'negociacion'].includes(q.status || 'nuevo')).length;
  const kwp = scope.reduce((s, q) => s + parseFloat(q.results?.actKwp || 0), 0).toFixed(1);
  const inversion = scope.reduce((s, q) => s + (q.budget?.tot || 0), 0);

  // KPIs por rol
  const kpis = esCliente
    ? [
        { label: 'Mis cotizaciones', value: total },
        { label: 'En gestión', value: enGestion },
        { label: 'Ganadas', value: ganadas },
        { label: 'Inversión total', value: fmtCOP(inversion) },
      ]
    : [
        { label: 'Cotizaciones', value: total },
        { label: 'kWp cotizados', value: kwp },
        { label: 'Pipeline', value: fmtCOP(inversion) },
        { label: 'Servicios', value: SERVICE_CATALOG.length },
      ];

  // Accesos (tiles) por rol — sin emojis.
  // 🔴 El producto PRINCIPAL de Solar Hub es el estudio, no la cotización gratis.
  // Medido: el primer tile y el botón de cabecera iban a `quoter`, así que el
  // invitado «siempre veía el cotizador gratuito por defecto».
  const tiles = [];
  const ctxEstudio = { quotes, ordenes };
  if (aud !== 'supplier') tiles.push({
    title: 'Estudio fotovoltaico', accent: C.teal,
    badge: 'Principal', badgeColor: C.teal,
    desc: 'Ingeniería de detalle: pérdidas, P50/P90, unifilar y memoria técnica.', onClick: () => go('estudio'),
  });
  tiles.push({
    title: 'Cotización rápida', accent: C.yellow, badge: 'Gratis', badgeColor: C.yellow,
    desc: 'Techo Google Solar, PVGIS y CREG. De ahí sale el kit a vender.', onClick: () => go('quoter'),
  });
  tiles.push({
    title: 'Referencias de equipos', accent: C.yellow, badge: 'Sin stock', badgeColor: C.amber,
    desc: 'Paneles, inversores y medidores de referencia. El kit se arma en el cotizador.', onClick: () => go('tienda'),
  });
  if (veMonitoreo(claims, ctxEstudio)) tiles.push({
    title: 'Monitoreo de planta', accent: C.teal,
    badge: 'Equipos', badgeColor: C.teal,
    desc: 'Fichas de tus equipos AMI. Solar Hub no abre la consola de FluxAI.', onClick: () => go('monitoreo'),
  });
  if (esCliente) tiles.push({
    title: 'Mis cotizaciones', accent: C.teal, badge: total ? `${total}` : 'Nuevo', badgeColor: C.teal,
    desc: 'Consulta el estado de tus estudios solares y propuestas.', onClick: () => go('misCotizaciones'),
  });
  if (aud === 'supplier') tiles.push({
    title: 'Portal de proveedores', accent: C.amber, badge: 'Marketplace', badgeColor: C.amber,
    desc: 'Inventario, órdenes de compra y comisiones.', onClick: () => go('proveedor'),
  });
  if (esAdmin || esOper) tiles.push({
    title: 'Administración', accent: C.teal, badge: 'Admin', badgeColor: C.teal,
    desc: 'Cotizaciones, asignación de técnicos, órdenes a proveedores y comisiones.', onClick: () => go('backoffice'),
  });

  return (
    <div className="al-content sh-fade-up" style={{ maxWidth: 1240, margin: '0 auto', padding: '16px 18px 32px', width: '100%' }}>
      {/* Cabecera del panel */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div style={{ borderLeft: `3px solid ${C.teal}`, paddingLeft: 10 }}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.heading, letterSpacing: '-0.3px' }}>
            {nombre ? `Hola, ${nombre}` : 'Panel'}
          </h1>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 1 }}>
            {esCliente ? 'Tu energía solar de extremo a extremo — diseño, cotización y monitoreo.'
              : esAdmin || esOper ? 'Monitor de la plataforma y de todos los servicios de la suite.'
              : 'Suite Alebas — diseño solar (Solar Hub) + telemetría en tiempo real (FluxAI).'}
            {claims && <> · <strong style={{ color: C.heading }}>{profileLabel(claims)}</strong></>}
          </div>
        </div>
        <button type="button" className="sh-btn sh-btn-primary sh-btn-sm" onClick={() => go(aud === 'supplier' ? 'proveedor' : 'estudio')}>
          {aud === 'supplier' ? 'Portal →' : 'Estudio fotovoltaico →'}
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 16 }}>
        {kpis.map((k, i) => <Kpi key={i} {...k} />)}
      </div>

      {/* Accesos por rol */}
      <SectionTitle>Accesos</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 8, marginBottom: 18 }}>
        {tiles.map((t, i) => <Tile key={i} {...t} />)}
      </div>

      {/* TUS SERVICIOS — acceso rápido a los servicios CONTRATADOS (gateo audiencia × entitlement).
          Los de FluxAI abren EMBEBIDOS (misma sesión); los de Solar Hub por su ruta. */}
      {(() => {
        const mis = serviciosAccesibles(claims).filter(s => s.sku !== 'cotizacion_rapida');
        if (!mis.length) return null;
        return (
          <>
            <SectionTitle>Tus servicios · acceso rápido</SectionTitle>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
              {mis.map(s => {
                const esFlux = s.suite === 'fluxai';
                return (
                  <button type="button" key={s.sku} className="sh-btn sh-btn-outline" onClick={() => (s.ruta ? go(s.ruta) : (openFlux && openFlux(s.sku)))}
                    title={`${s.nombre}${esFlux ? ' · potenciado por FluxAI' : ''}`}
                    style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12.5, color: C.heading }}>
                    <span style={{ width: 22, height: 22, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: C.teal, background: `${C.teal}14`, border: `1px solid ${C.teal}33` }}><ServiceIcon sku={s.sku} size={14} /></span>
                    {s.nombre}
                    {esFlux && <span style={{ fontSize: 8.5, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 5px' }}>FluxAI</span>}
                  </button>
                );
              })}
            </div>
          </>
        );
      })()}

      <SectionTitle right={<button type="button" onClick={() => go('tienda')} style={{ background: 'transparent', border: 'none', color: C.yellow, fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>Ver referencias →</button>}>
        Referencias (sin inventario)
      </SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginBottom: 18 }}>
        {CATEGORIAS.filter((c) => ['panel', 'inversor', 'medidor', 'bateria', 'modem'].includes(c.id)).map((c) => (
          <button key={c.id} type="button" onClick={() => go('tienda')}
            style={{ textAlign: 'left', padding: 0, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', background: C.card, cursor: 'pointer' }}>
            <img src={c.img} alt="" style={{ width: '100%', height: 72, objectFit: 'cover', display: 'block' }} />
            <div style={{ padding: '8px 10px' }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.heading }}>{c.label}</div>
              <div style={{ fontSize: 10.5, color: C.muted }}>{c.blurb}</div>
            </div>
          </button>
        ))}
      </div>

      {/* MONITOR DE SERVICIOS — solo admin/operador (administra/monitorea TODOS los servicios) */}
      {(esAdmin || esOper) && (
        <>
          <SectionTitle right={<LinkBtn onClick={() => go('servicios')}>Ver catálogo →</LinkBtn>}>Monitor de servicios</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 8, marginBottom: 18 }}>
            {SERVICE_CATALOG.map(s => {
              const esFlux = s.suite === 'fluxai';
              return (
                <div key={s.sku} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px', borderLeft: `3px solid ${C.teal}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: C.heading }}>{s.nombre}</span>
                    <span style={{ fontSize: 8.5, fontWeight: 700, color: C.green, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.green, display: 'inline-block' }} />OPERATIVO
                    </span>
                  </div>
                  {/* Badges neutros (sober): la procedencia y el plan en gris, sin sumar colores. */}
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', color: C.muted, background: `${C.muted}14`, border: `1px solid ${C.border}`, borderRadius: 4, padding: '2px 6px' }}>{esFlux ? 'FluxAI' : 'Solar Hub'}</span>
                    <span style={{ fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', color: C.muted, background: `${C.muted}14`, border: `1px solid ${C.border}`, borderRadius: 4, padding: '2px 6px' }}>{s.tier === 'premium' ? 'Premium' : 'Gratis'}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* TRACKING — últimas cotizaciones (lo que genera el sistema) */}
          <SectionTitle right={<LinkBtn onClick={() => go('backoffice')}>Gestionar en Administración →</LinkBtn>}>Tracking de cotizaciones</SectionTitle>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 14px 12px', marginBottom: 18, overflowX: 'auto' }}>
            {scope.length === 0 ? (
              <div style={{ fontSize: 12, color: C.muted, padding: '14px 0', textAlign: 'center' }}>Sin cotizaciones registradas todavía.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                <thead><tr>{['Fecha', 'Cliente', 'Sistema', 'kWp', 'Estado', 'Inversión'].map(h =>
                  <th key={h} style={{ textAlign: 'left', fontSize: 9.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, padding: '8px 6px', borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
                <tbody>
                  {[...scope].slice(0, 8).map((q, i) => {
                    const est = q.status || 'nuevo';
                    const col = est === 'ganada' || est === 'cerrada' ? C.green : est === 'perdida' ? C.red : C.muted;
                    return (
                      <tr key={q.id || i}>
                        <td style={{ fontSize: 11, color: C.muted, padding: '8px 6px', borderBottom: `1px solid ${C.border}33` }}>{q.date ? new Date(q.date).toLocaleDateString('es-CO') : '—'}</td>
                        <td style={{ fontSize: 11.5, color: C.heading, fontWeight: 600, padding: '8px 6px', borderBottom: `1px solid ${C.border}33` }}>{q.name || q.company || '—'}</td>
                        <td style={{ fontSize: 11, color: C.muted, padding: '8px 6px', borderBottom: `1px solid ${C.border}33`, textTransform: 'capitalize' }}>{q.systemType || '—'}</td>
                        <td style={{ fontSize: 11, color: C.text, padding: '8px 6px', borderBottom: `1px solid ${C.border}33` }}>{q.results?.actKwp ?? '—'}</td>
                        <td style={{ padding: '8px 6px', borderBottom: `1px solid ${C.border}33` }}>
                          <span style={{ fontSize: 9.5, fontWeight: 700, color: col, background: `${col}18`, border: `1px solid ${col}44`, borderRadius: 5, padding: '2px 8px', textTransform: 'capitalize' }}>{est}</span>
                        </td>
                        <td style={{ fontSize: 11.5, color: C.yellow, fontWeight: 700, padding: '8px 6px', borderBottom: `1px solid ${C.border}33` }}>{fmtCOP(q.budget?.tot)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Flujo extremo a extremo */}
      <SectionTitle>Del estudio al monitoreo</SectionTitle>
      <div style={{ fontSize: 11.5, color: C.muted, margin: '-4px 0 8px' }}>Del diseño solar de Solar Hub al monitoreo en tiempo real potenciado por FluxAI — una sola casa: Alebas Ingeniería.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
        {FLOW.map(([t, d], i) => (
          <Card key={i}>
            <span style={{ fontSize: 10, fontWeight: 800, color: C.teal }}>{`0${i + 1}`}</span>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.heading, marginTop: 4 }}>{t}</div>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4, marginTop: 4 }}>{d}</div>
          </Card>
        ))}
      </div>

      <div style={{ marginTop: 22, fontSize: 10, color: C.muted, textAlign: 'center' }}>
        Solar Hub · creado por ALEBAS Ingeniería SAS · Ley 1715 · CREG 174/2021 · RETIE
      </div>
    </div>
  );
}
